import { useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export interface ServiceRequestItem {
  id: number;
  table_id: number;
  type: 'CALL_WAITER' | 'REQUEST_BILL';
  payment_method?: 'CASH' | 'CARD' | 'TRANSFER' | null;
  notes?: string | null;
  status: 'PENDING' | 'ATTENDED' | 'CANCELLED';
  created_at: string;
  table?: {
    id: number;
    number: number | string;
    name?: string;
  };
}

/**
 * Genera un sonido agradable de timbre de mesero (dos notas claras C6 -> G6)
 */
function playServiceBellSound() {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();

    // Campana Nota 1 (C6 - 1046.5 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(1046.5, ctx.currentTime);
    gain1.gain.setValueAtTime(0.4, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start();
    osc1.stop(ctx.currentTime + 0.45);

    // Campana Nota 2 (G6 - 1567.98 Hz)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1567.98, ctx.currentTime + 0.15);
    gain2.gain.setValueAtTime(0.35, ctx.currentTime + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(ctx.currentTime + 0.15);
    osc2.stop(ctx.currentTime + 0.7);
  } catch {
    // Si la política de audio del navegador lo bloquea, se ignora silenciosamente
  }
}

export function useServiceRequests(pollIntervalMs = 6000) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const prevCountRef = useRef<number | null>(null);

  const { data: requests = [], refetch, isLoading } = useQuery<ServiceRequestItem[]>({
    queryKey: ['serviceRequestsActive'],
    queryFn: async () => {
      const { data } = await api.get('/service-requests/active');
      return data.data || [];
    },
    enabled: !!token,
    refetchInterval: pollIntervalMs,
    staleTime: 3000,
  });

  // Detección de nuevas solicitudes para reproducir sonido y notificar
  useEffect(() => {
    if (requests && prevCountRef.current !== null) {
      if (requests.length > prevCountRef.current) {
        // Hay una o más solicitudes nuevas
        const newest = requests[0];
        const tableNum = newest?.table?.number ?? newest?.table_id;
        const isCall = newest?.type === 'CALL_WAITER';

        playServiceBellSound();

        toast.info(
          `🛎️ Mesa #${tableNum}: ${isCall ? '¡Llamando al mesero!' : 'Pide la cuenta'}`,
          {
            autoClose: 5000,
            icon: isCall ? '🙋‍♂️' : '💳',
          }
        );
      }
    }
    prevCountRef.current = requests ? requests.length : 0;
  }, [requests]);

  const attendMutation = useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post(`/service-requests/${id}/attend`);
      return data;
    },
    onSuccess: (_, id) => {
      // Optimistic update
      queryClient.setQueryData<ServiceRequestItem[]>(['serviceRequestsActive'], old => {
        return (old || []).filter(r => r.id !== id);
      });
      queryClient.invalidateQueries({ queryKey: ['serviceRequestsActive'] });
      toast.success('Solicitud marcada como atendida');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || 'Error al atender solicitud';
      toast.error(msg);
    },
  });

  return {
    requests,
    activeCount: requests.length,
    attendRequest: (id: number) => attendMutation.mutateAsync(id),
    isAttending: attendMutation.isPending,
    refetch,
    isLoading,
  };
}

export default useServiceRequests;
